import * as React from 'react';
import { useQuery } from '@tanstack/react-query';
import { 
  Card, 
  Title, 
  Select, 
  SelectItem,
  Grid,
  Text,
  Metric,
  Badge,
  Table,
  TableHead,
  TableHeaderCell,
  TableBody,
  TableRow,
  TableCell,
} from '@tremor/react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import { supabase } from '../../lib/supabase';

interface DevFinancial {
  fin_inc_amt: number;
  fin_exp_amt: number;
  fin_comm: string;
  fin_desc: string;
  fin_supplier: string;
  fin_date: string;
  created_at: string;
}

interface FinancialAnalyticsProps {
  className?: string;
}

type FinancialYear = 'all' | string; // e.g. '2024-2025'
type FinancialYearOption = {
  value: FinancialYear;
  label: string;
};

export default function FinancialAnalytics({ className }: FinancialAnalyticsProps) {
  const [selectedCommunity, setSelectedCommunity] = React.useState<string>('all');
  const [selectedDescription, setSelectedDescription] = React.useState<string>('all');
  const [selectedSupplier, setSelectedSupplier] = React.useState<string>('all');
  const [selectedFinancialYear, setSelectedFinancialYear] = React.useState<FinancialYear>('all');

  const generateFinancialYearOptions = (): FinancialYearOption[] => {
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    const currentMonth = currentDate.getMonth();
    
    // If we're in the first half of the calendar year, the current FY started last year
    const currentFY = currentMonth < 6 ? currentYear - 1 : currentYear;
    
    const options: FinancialYearOption[] = [
      { value: 'all', label: 'All Time' }
    ];

    // Generate last 5 financial years
    for (let i = 0; i < 5; i++) {
      const startYear = currentFY - i;
      const endYear = startYear + 1;
      options.push({
        value: `${startYear}-${endYear}`,
        label: `FY ${startYear}-${endYear}`
      });
    }

    return options;
  };

  const financialYearOptions = generateFinancialYearOptions();

  const getDateRangeForFinancialYear = (fy: FinancialYear): { from?: string; to?: string } => {
    if (fy === 'all') return {};
    
    const [startYear, endYear] = fy.split('-').map(Number);
    
    // Validate years
    if (isNaN(startYear) || isNaN(endYear)) {
      console.error('Invalid financial year format:', fy);
      return {};
    }
    
    // Create dates at start of day (UTC) to ensure consistent handling
    const from = new Date(Date.UTC(startYear, 6, 1, 0, 0, 0)); // July 1st
    const to = new Date(Date.UTC(endYear, 5, 30, 23, 59, 59)); // June 30th
    
    return {
      from: from.toISOString(),
      to: to.toISOString()
    };
  };

  // Fetch financial data
  const { data: financials = [], isLoading, error } = useQuery<DevFinancial[]>({
    queryKey: ['financials', selectedCommunity, selectedDescription, selectedSupplier, selectedFinancialYear],
    queryFn: async () => {
      if (selectedFinancialYear !== 'all') {
        const yearPattern = /^\d{4}-\d{4}$/;
        if (!yearPattern.test(selectedFinancialYear)) {
          throw new Error('Invalid financial year format. Expected format: YYYY-YYYY');
        }
      }

      try {
        let query = supabase
          .from('dev_financials')
          .select('*');

        // Apply filters
        if (selectedCommunity !== 'all') {
          query = query.eq('fin_comm', selectedCommunity);
        }
        if (selectedDescription !== 'all') {
          query = query.eq('fin_desc', selectedDescription);
        }
        if (selectedSupplier !== 'all') {
          query = query.eq('fin_supplier', selectedSupplier);
        }

        // Apply time period filter
        const { from, to } = getDateRangeForFinancialYear(selectedFinancialYear);
        if (from && to) {
          query = query.gte('fin_date', from)
                      .lte('fin_date', to);
        }

        const { data, error } = await query;
        
        if (error) {
          throw new Error(`Failed to fetch financial data: ${error.message}${error.hint ? ` (Hint: ${error.hint})` : ''}`);
        }

        console.log('Raw financial data:', data?.map(d => ({
          community: d.fin_comm,
          income: d.fin_inc_amt,
          expense: d.fin_exp_amt
        })));

        return data || [];
      } catch (error) {
        console.error('Error in financial data fetch:', error);
        throw error;
      }
    }
  });

  // Get unique values for filters
  const communities = React.useMemo(() => 
    Array.from(new Set(financials.map(f => f.fin_comm))).sort(),
    [financials]
  );

  const descriptions = React.useMemo(() => 
    Array.from(new Set(financials.map(f => f.fin_desc))).sort(),
    [financials]
  );

  const suppliers = React.useMemo(() => 
    Array.from(new Set(financials.map(f => f.fin_supplier))).sort(),
    [financials]
  );

  // Calculate summary statistics
  const summaryStats = React.useMemo(() => {
    console.log('Calculating summary stats from financials:', financials.map(f => ({
      community: f.fin_comm,
      income: f.fin_inc_amt,
      expense: f.fin_exp_amt
    })));

    const incomeTotal = financials.reduce((sum, curr) => sum + (curr.fin_inc_amt || 0), 0);
    const incomeAvgPerCommunity = incomeTotal / (communities.length || 1);
    const expenseTotal = financials.reduce((sum, curr) => sum + (curr.fin_exp_amt || 0), 0);
    const expenseAvgPerCommunity = expenseTotal / (communities.length || 1);

    console.log('Summary stats calculated:', {
      incomeTotal,
      incomeAvgPerCommunity,
      expenseTotal,
      expenseAvgPerCommunity
    });

    return {
      income: {
        total: incomeTotal,
        avgPerCommunity: incomeAvgPerCommunity
      },
      expense: {
        total: expenseTotal,
        avgPerCommunity: expenseAvgPerCommunity
      }
    };
  }, [financials, communities.length]);

  // Prepare chart data
  const chartData = React.useMemo(() => {
    console.log('Preparing chart data from financials:', financials.map(f => ({
      community: f.fin_comm,
      income: f.fin_inc_amt,
      expense: f.fin_exp_amt
    })));

    const communityTotals = financials.reduce((acc, curr) => {
      const existing = acc.find(item => item.community === curr.fin_comm);
      if (existing) {
        existing.income += curr.fin_inc_amt || 0;
        existing.expense += curr.fin_exp_amt || 0;
      } else {
        acc.push({ 
          community: curr.fin_comm, 
          income: curr.fin_inc_amt || 0,
          expense: curr.fin_exp_amt || 0
        });
      }
      return acc;
    }, [] as { community: string; income: number; expense: number }[]);

    const sortedData = communityTotals.sort((a, b) => b.income - a.income);
    
    console.log('Final chart data:', sortedData);
    
    return sortedData;
  }, [financials]);

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  };

  if (isLoading) {
    return (
      <Card
        className={`${className} bg-white`}
        decoration="top"
        decorationColor="orange"
      >
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-1/4"></div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-24 bg-gray-200 rounded"></div>
            ))}
          </div>
          <div className="h-64 bg-gray-200 rounded"></div>
        </div>
      </Card>
    );
  }

  if (error) {
    return (
      <Card
        className={`${className} bg-white`}
        decoration="top"
        decorationColor="orange"
      >
        <div className="p-4 bg-red-50 text-red-700 rounded-lg border border-red-200">
          <p className="font-semibold">Error loading financial data</p>
          <p className="text-sm mt-1">{error instanceof Error ? error.message : 'Unknown error occurred'}</p>
          <p className="text-xs mt-2 text-red-500">Please check the browser console for more details.</p>
        </div>
      </Card>
    );
  }

  return (
    <Card
      className={`${className} bg-white`}
      decoration="top"
      decorationColor="orange"
    >
      <div className="space-y-8">
        <div>
          <Title>Financial Analysis</Title>
          <Text>Income and expense analysis by community and filters</Text>
        </div>

        {/* Financial Year Selector */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Text className="font-medium">Financial Year</Text>
            {selectedFinancialYear !== 'all' && (
              <Badge size="sm" color="orange">
                {financialYearOptions.find(opt => opt.value === selectedFinancialYear)?.label}
              </Badge>
            )}
          </div>
          <Select
            value={selectedFinancialYear}
            onValueChange={(value) => setSelectedFinancialYear(value as FinancialYear)}
            placeholder="Click here to select a Financial Year"
            className="max-w-xs"
            defaultValue="all"
            color="gray"
            icon={undefined}
          >
            {financialYearOptions.map(option => (
              <SelectItem
                key={option.value}
                value={option.value}
              >
                {option.value === selectedFinancialYear ? `✓ ${option.label}` : option.label}
              </SelectItem>
            ))}
          </Select>
        </div>

        {/* Other Filters */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Select
            value={selectedCommunity}
            onValueChange={setSelectedCommunity}
            placeholder="Select Community"
            className="w-full"
            defaultValue="all"
            color="gray"
            icon={undefined}
          >
            <SelectItem value="all">All Communities</SelectItem>
            {communities.map(community => (
              <SelectItem key={community} value={community}>
                {community}
              </SelectItem>
            ))}
          </Select>

          <Select
            value={selectedDescription}
            onValueChange={setSelectedDescription}
            placeholder="Select Description"
            className="w-full"
            defaultValue="all"
            color="gray"
            icon={undefined}
          >
            <SelectItem value="all">All Descriptions</SelectItem>
            {descriptions.map(desc => (
              <SelectItem key={desc} value={desc}>
                {desc}
              </SelectItem>
            ))}
          </Select>

          <Select
            value={selectedSupplier}
            onValueChange={setSelectedSupplier}
            placeholder="Select Supplier"
            className="w-full"
            defaultValue="all"
            color="gray"
            icon={undefined}
          >
            <SelectItem value="all">All Suppliers</SelectItem>
            {suppliers.map(supplier => (
              <SelectItem key={supplier} value={supplier}>
                {supplier}
              </SelectItem>
            ))}
          </Select>
        </div>

        {/* Summary Statistics */}
        <div className="space-y-4">
          {/* Income Statistics */}
          <Grid numItems={1} numItemsSm={2} numItemsLg={2} className="gap-4">
            <Card decoration="top" decorationColor="blue">
              <Text>Total Income</Text>
              <Metric>{formatCurrency(summaryStats.income.total)}</Metric>
            </Card>
            <Card decoration="top" decorationColor="blue">
              <Text>Average per Community</Text>
              <Metric>{formatCurrency(summaryStats.income.avgPerCommunity)}</Metric>
            </Card>
          </Grid>

          {/* Expense Statistics */}
          <Grid numItems={1} numItemsSm={2} numItemsLg={2} className="gap-4">
            <Card decoration="top" decorationColor="rose">
              <Text>Total Expenses</Text>
              <Metric>{formatCurrency(summaryStats.expense.total)}</Metric>
            </Card>
            <Card decoration="top" decorationColor="rose">
              <Text>Average per Community</Text>
              <Metric>{formatCurrency(summaryStats.expense.avgPerCommunity)}</Metric>
            </Card>
          </Grid>
        </div>

        {chartData.length > 0 ? (
          <>
            {/* Bar Chart */}
            <Card>
              <Title>Financial Summary by Community</Title>
              <div className="mt-4">
                <div className="w-full" style={{ 
                  height: `min(${window.innerWidth < 768 ? '120vw' : '60vh'}, ${window.innerWidth < 768 ? '500px' : '600px'})` 
                }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={chartData}
                      margin={window.innerWidth < 768 ? {
                        top: 20,
                        right: 20,
                        left: 40,
                        bottom: 100,
                      } : {
                        top: 20,
                        right: 30,
                        left: 60,
                        bottom: 20,
                      }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis 
                        dataKey="community" 
                        angle={window.innerWidth < 768 ? -65 : -45}
                        textAnchor="end"
                        height={window.innerWidth < 768 ? 120 : 80}
                        interval={0}
                        tick={{ 
                          fontSize: window.innerWidth < 768 ? 10 : 12,
                          dy: window.innerWidth < 768 ? 10 : 0
                        }}
                        padding={{ left: 20, right: 20 }}
                      />
                      <YAxis 
                        tickFormatter={formatCurrency}
                        width={window.innerWidth < 768 ? 60 : 100}
                        tick={{ fontSize: window.innerWidth < 768 ? 10 : 12 }}
                        padding={{ top: 20, bottom: 20 }}
                      />
                      <Tooltip 
                        formatter={(value) => formatCurrency(value as number)}
                        labelStyle={{ color: '#374151' }}
                        contentStyle={{ 
                          backgroundColor: 'white',
                          border: '1px solid #E5E7EB',
                          borderRadius: '0.375rem',
                          fontSize: window.innerWidth < 768 ? '12px' : '14px',
                          padding: window.innerWidth < 768 ? '8px' : '12px'
                        }}
                        wrapperStyle={{
                          zIndex: 1000,
                          maxWidth: window.innerWidth < 768 ? '80vw' : 'none'
                        }}
                      />
                      <Legend />
                      <Bar dataKey="income" name="Income" fill="#0000FF" />
                      <Bar dataKey="expense" name="Expenses" fill="#cb734b" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </Card>

            {/* Data Table */}
            <Card>
              <Title>Detailed Financial Breakdown</Title>
              <Table className="mt-4">
                <TableHead>
                  <TableRow>
                    <TableHeaderCell>Community</TableHeaderCell>
                    <TableHeaderCell>Total Income</TableHeaderCell>
                    <TableHeaderCell>Total Expenses</TableHeaderCell>
                    <TableHeaderCell>Net Position</TableHeaderCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {chartData.map((item) => (
                    <TableRow key={item.community}>
                      <TableCell>{item.community}</TableCell>
                      <TableCell>{formatCurrency(item.income)}</TableCell>
                      <TableCell>{formatCurrency(item.expense)}</TableCell>
                      <TableCell>
                        <Text color={item.income - item.expense >= 0 ? "blue" : "rose"}>
                          {formatCurrency(item.income - item.expense)}
                        </Text>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
          </>
        ) : (
          <Card>
            <div className="h-80 flex items-center justify-center bg-gray-50 rounded-lg border border-gray-200">
              <div className="text-center">
                <p className="mb-2">No financial data available</p>
                <p className="text-sm text-gray-500">
                  {financials.length === 0
                    ? "No records found in the dev_financials table"
                    : "No data matches the selected filters"}
                </p>
              </div>
            </div>
          </Card>
        )}
      </div>
    </Card>
  );
}
